import { ActionType, createAsyncAction } from "typesafe-actions";

import { OnlyRequiredFields } from "@mapmycustomers/shared";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { RawFile } from "@mapmycustomers/shared/types/File";
import User from "@mapmycustomers/shared/types/User";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";

import { ResourcePayload } from "@app/store/api/ApiService";
import FileListItem from "@app/types/FileListItem";

import { ExternalResourcesActions } from "./externalResources";
import ResourcesViewState from "./ResourcesViewState";

export const initialize = createAsyncAction(
  "resources/initialize/request",
  "resources/initialize/success",
  "resources/initialize/failure"
)<void, { folders: Folder[]; resources: Resource[]; storageUsed: number }, void>();

export const fetchResources = createAsyncAction(
  "resources/fetchResources/request",
  "resources/fetchResources/success",
  "resources/fetchResources/failure"
)<
  Partial<ResourcesViewState>,
  { resources: Resource[]; storageUsed: number; totalResources: number },
  void
>();

export const fetchResource = createAsyncAction(
  "resources/fetchResource/request",
  "resources/fetchResource/success",
  "resources/fetchResource/failure"
)<
  {
    failureCallback?: (error: ApiError) => void;
    id: Resource["id"];
    options?: {
      includeTemplates?: boolean;
      includeTrash?: boolean;
    };
    successCallback?: (resource: Resource) => void;
  },
  void,
  void
>();

export const uploadFiles = createAsyncAction(
  "resources/uploadFiles/request",
  "resources/uploadFiles/success",
  "resources/uploadFiles/failure"
)<
  {
    callback?: (filesList: FileListItem[]) => void;
    files: (Blob | File)[];
  },
  FileListItem[],
  void
>();

export const removeFile = createAsyncAction(
  "resources/removeFile/request",
  "resources/removeFile/success",
  "resources/removeFile/failure"
)<RawFile, RawFile["id"], void>();

export const bulkCreateResources = createAsyncAction(
  "resources/bulkCreateResources/request",
  "resources/bulkCreateResources/success",
  "resources/bulkCreateResources/failure"
)<
  {
    files: RawFile[];
    folderId?: Folder["id"];
    isPublic?: boolean;
    linkName?: string;
    successCallback?: () => void;
    url?: string;
    userIds: User["id"][];
  },
  void,
  void
>();

export const createFolder = createAsyncAction(
  "resources/createFolder/request",
  "resources/createFolder/success",
  "resources/createFolder/failure"
)<
  { callback?: (id: Folder["id"]) => void; name: Folder["name"]; parentFolderId?: Folder["id"] },
  Folder,
  void
>();

export const updateFolder = createAsyncAction(
  "resources/updateFolder/request",
  "resources/updateFolder/success",
  "resources/updateFolder/failure"
)<{ callback?: () => void; folder: OnlyRequiredFields<Folder, "id"> }, Folder, void>();

export const deleteFolder = createAsyncAction(
  "resources/deleteFolder/request",
  "resources/deleteFolder/success",
  "resources/deleteFolder/failure"
)<{ callback?: () => void; id: Folder["id"] }, Folder["id"], void>();

export const updateResource = createAsyncAction(
  "resources/updateResource/request",
  "resources/updateResource/success",
  "resources/updateResource/failure"
)<
  {
    resource: ResourcePayload;
    restore?: boolean;
    successCallback?: () => void;
    toTrash?: boolean;
  },
  void,
  void
>();

export const deleteResource = createAsyncAction(
  "resources/deleteResource/request",
  "resources/deleteResource/success",
  "resources/deleteResource/failure"
)<Pick<Folder, "id" | "name"> & { isTrash?: boolean }, void, void>();

export const emptyTrashFolder = createAsyncAction(
  "resources/emptyTrashFolder/request",
  "resources/emptyTrashFolder/success",
  "resources/emptyTrashFolder/failure"
)<void, void, void>();

export const bulkEditFolders = createAsyncAction(
  "resources/bulkEditFolders/request",
  "resources/bulkEditFolders/success",
  "resources/bulkEditFolders/failure"
)<{ callback?: () => void; folders: OnlyRequiredFields<Folder, "id">[] }, void, void>();

export const bulkDeleteFolders = createAsyncAction(
  "resources/bulkDeleteFolders/request",
  "resources/bulkDeleteFolders/success",
  "resources/bulkDeleteFolders/failure"
)<{ callback?: () => void; ids: Folder["id"][] }, Folder["id"][], void>();

export const bulkEditResources = createAsyncAction(
  "resources/bulkEditResources/request",
  "resources/bulkEditResources/success",
  "resources/bulkEditResources/failure"
)<
  { callback?: () => void; resources: ResourcePayload[]; restore?: boolean; toTrash?: boolean },
  void,
  void
>();

export const bulkDeleteResources = createAsyncAction(
  "resources/bulkDeleteResources/request",
  "resources/bulkDeleteResources/success",
  "resources/bulkDeleteResources/failure"
)<{ callback?: () => void; ids: Resource["id"][]; isTrash?: boolean }, void, void>();

export const fetchFolders = createAsyncAction(
  "resources/fetchFolders/request",
  "resources/fetchFolders/success",
  "resources/fetchFolders/failure"
)<void, Folder[], void>();

export const fetchPinnedResources = createAsyncAction(
  "resources/fetchPinnedResources/request",
  "resources/fetchPinnedResources/success",
  "resources/fetchPinnedResources/failure"
)<Resource["id"][] | void, Resource[], void>();

export const pinUnpinResource = createAsyncAction(
  "resources/pinUnpinResource/request",
  "resources/pinUnpinResource/success",
  "resources/pinUnpinResource/failure"
)<{ ids: Resource["id"][]; unpin?: boolean }, Resource[], void>();

export const updatePinnedResources = createAsyncAction(
  "resources/updatePinnedResources/request",
  "resources/updatePinnedResources/success",
  "resources/updatePinnedResources/failure"
)<{ callback?: () => void; ids: Resource["id"][] }, void, void>();

export const fetchTrashResourcesCount = createAsyncAction(
  "@resources/fetchTrashResourcesCount/request",
  "@resources/fetchTrashResourcesCount/success",
  "@resources/fetchTrashResourcesCount/failure"
)<void, number, void>();

export type Actions =
  | ActionType<
      | typeof bulkCreateResources
      | typeof bulkDeleteFolders
      | typeof bulkDeleteResources
      | typeof bulkEditFolders
      | typeof bulkEditResources
      | typeof createFolder
      | typeof deleteFolder
      | typeof deleteResource
      | typeof emptyTrashFolder
      | typeof fetchFolders
      | typeof fetchPinnedResources
      | typeof fetchResource
      | typeof fetchResources
      | typeof fetchTrashResourcesCount
      | typeof initialize
      | typeof pinUnpinResource
      | typeof removeFile
      | typeof updateFolder
      | typeof updatePinnedResources
      | typeof updateResource
      | typeof uploadFiles
    >
  | ExternalResourcesActions;
